import React, { FunctionComponent } from 'react'
import { useIntl } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { SELF_PAY_ZENDESK_ARTICLE_URL } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'

import { BodyText, Size as BodyTextSize } from '../../atoms/bodyText/BodyText'
import { InlineWarningBanner } from '../../molecules/inlineWarningBanner/InlineWarningBanner'
import { openUrl } from '../../utils/expo-linking'
import { Link } from '../link/Link'

const InlineText = styled(BodyText)`
  display: contents;
`

export type HPISelfPayProviderBannerProps = {
  variant: 'paymentMissing' | 'needsInfoOrEnrollment'
  clientFirstName: string
  customerName: string
}

// Used to display an InlineWarningBanner when the customer is HPI only and needs to add a payment method.
export const HPISelfPayProviderBanner: FunctionComponent<HPISelfPayProviderBannerProps> = ({
  variant,
  clientFirstName,
  customerName,
}) => {
  const { formatMessage } = useIntl()
  const { colors } = useTheme()

  const warning = (chunks: string) => (
    <InlineText bold color={colors.textWarning} size={BodyTextSize.SMALL} text={chunks} />
  )
  const link = (chunks: string) => (
    <Link
      onPress={() => openUrl(SELF_PAY_ZENDESK_ARTICLE_URL)}
      size={BodyTextSize.SMALL}
      style={{ textDecorationLine: 'underline', color: colors.textWarning }}
      text={chunks}
    />
  )

  const bannerText =
    variant === 'paymentMissing'
      ? formatMessage(
          {
            defaultMessage:
              '{clientFirstName} has entered their health plan information but is <warning>missing their payment method for cost sharing</warning>. Instruct your client to update their cost sharing payment method in the health plan section of their profile.',
            description:
              'Provider banner when HPI-supported self-pay-eligible client has eligible health plan but is missing the cost share payment method',
          },
          { clientFirstName, warning },
        )
      : formatMessage(
          {
            defaultMessage:
              '{clientFirstName} <warning>needs to enter their health plan and payment information or enroll in self-pay</warning> to continue care. {customerName} offers a self-pay option for clients to pay for sessions themselves. <link>Learn more about self-pay here.</link>',
            description:
              'Provider banner when HPI-supported self-pay-eligible client has neither an eligible health plan nor self-pay set up',
          },
          { clientFirstName, customerName, warning, link },
        )

  return (
    <InlineWarningBanner
      testId={'HPISelfPayProviderBanner'}
      text={bannerText}
      textSize={BodyTextSize.SMALL}
      textColor={colors.textWarning}
      iconColor={colors.textWarning}
      marginBottom='16px'
    />
  )
}
